import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRef, useCallback, useMemo } from 'react';
import { useModelBaseView } from '@/components/Recipe/FlowContext';
import { ImageList } from '@/components/Common/ImageList/ImageList';
import { NodeViewMode } from '@/enums/node-view-mode.enum';
import { useBatchView } from '@/hooks/nodes/useBatchView';
import GalleryGradientOverlay from '@/components/Common/GalleryGradientOverlay';
import { FilesViewOverlay } from '@/components/Nodes/ModelComponents/FilesViewOverlay/FilesViewOverlay';
import { isTextAsset } from '@/components/Common/ImageList/utils';
import { ModelAssetToUploadedAsset } from '@/types/api/assets';
import { FlexCol } from '@/UI/styles';
import { renderEmptyState } from '../ModelNodesUtils';
import { getFileViewerControlsOffset, getAssetsTypeTranslationKey } from '../utils/nodeViewUtils';
import { useShowGallery } from './useShowGallery';
import { BatchNavigation } from './BatchNavigation';
import type { HandleType } from '@/enums/handle-type.enum';
import type { MenuAction } from '@/components/Menu/Actions';
import type { ThreeDProps } from '@/components/Common/ImageList/types';
import type { AiModelData } from '@/types/nodes/model';

interface ModelResultsProps {
  shouldUseLegacyFileViewer: boolean;
  coverImage?: string;
  data: AiModelData;
  id: string;
  isProcessing?: boolean;
  isResizable?: boolean;
  noEmptyState?: boolean;
  nodeOutputType?: HandleType;
  onTextChange?: (index: number, newValue: string) => void;
  selected: number;
  setSelected: React.Dispatch<React.SetStateAction<number>>;
  threeDProps?: ThreeDProps;
  viewMode: NodeViewMode;
  selectedViewMode: NodeViewMode;
  additionalContextMenuItems?: MenuAction[];
}

export const ModelResults = ({
  shouldUseLegacyFileViewer,
  coverImage,
  data,
  id,
  isProcessing = false,
  isResizable,
  noEmptyState = false,
  nodeOutputType,
  onTextChange,
  selected,
  setSelected,
  threeDProps,
  viewMode,
  selectedViewMode,
  additionalContextMenuItems,
}: ModelResultsProps) => {
  const { t } = useTranslation();
  const { isHovered, handleOpenGallery, ...elementProps } = useShowGallery(id);
  const imageLoadFailed = useRef(false);
  const modelBaseView = useModelBaseView(id);
  const allGenerations = useMemo(() => {
    return (
      data.generations?.map((generation, index) => ({
        ...ModelAssetToUploadedAsset(generation),
        globalIndex: index,
      })) ?? []
    );
  }, [data.generations]);

  const {
    generations,
    batchInfo,
    handlePreviousBatch,
    handleNextBatch,
    handleDownload,
    isAllBatchesHaveSingleGeneration,
  } = useBatchView({
    nodeId: id,
    nodeName: data.name || id,
    allGenerations,
    viewMode,
    selectedIndex: selected,
  });

  const handleSetViewMode = useCallback(
    (viewMode: NodeViewMode) => {
      modelBaseView.setViewMode(viewMode);
    },
    [modelBaseView],
  );

  const handleBack = useCallback(() => {
    if (generations.length === 0) return;
    const newIndex = selected === 0 ? generations.length - 1 : selected - 1;
    setSelected(newIndex);
  }, [generations.length, selected, setSelected]);

  const handleNext = useCallback(() => {
    if (generations.length === 0) return;
    const newIndex = selected === generations.length - 1 ? 0 : selected + 1;
    setSelected(newIndex);
  }, [generations.length, selected, setSelected]);

  const selectedFile = generations[selected];
  const maxSteps = generations.length;

  if (generations.length === 0) {
    if (coverImage && !imageLoadFailed.current) {
      return (
        <Box sx={{ width: '100%' }}>
          <img src={coverImage} width="100%" alt="model cover image" onError={() => (imageLoadFailed.current = true)} />
        </Box>
      );
    } else if (noEmptyState) {
      return null;
    } else {
      return renderEmptyState(nodeOutputType, isProcessing, t);
    }
  }

  const fileViewerControlsOffset = getFileViewerControlsOffset(viewMode, selectedFile);

  return (
    <FlexCol
      className={viewMode === NodeViewMode.Single && shouldUseLegacyFileViewer ? 'media-container-dark' : ''}
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
      }}
      {...elementProps}
    >
      <ImageList
        isHovered={isHovered}
        nodeName={data.name}
        allImages={allGenerations}
        images={generations}
        onTextChange={onTextChange}
        selected={selected}
        setSelected={setSelected}
        container="node"
        disabled={data.isLocked}
        threeDProps={generations.some((generation) => generation.type?.includes('3D')) ? threeDProps : undefined}
        fillHeight={isResizable && !!data.height}
        viewMode={viewMode}
        nodeId={id}
        shouldUseLegacyFileViewer={shouldUseLegacyFileViewer}
        additionalContextMenuItems={additionalContextMenuItems}
      />
      <GalleryGradientOverlay isSelected={isHovered} bottomOffset={fileViewerControlsOffset}>
        {viewMode === NodeViewMode.Single ? (
          <FilesViewOverlay
            asset={selectedFile}
            handleBack={handleBack}
            handleNext={handleNext}
            is3DLocked={threeDProps?.is3DLocked}
            showDownloadAllButton={!isTextAsset(selectedFile)}
            steps={maxSteps}
            onDownloadAll={handleDownload}
            onOpenGallery={handleOpenGallery}
            selected={selected}
            setIs3DLocked={threeDProps?.setIs3DLocked}
            viewMode={viewMode}
            setViewMode={handleSetViewMode}
            selectedViewMode={selectedViewMode}
            isModelNode={true}
            showExpandToGalleryButton={true}
            isAllBatchesHaveSingleGeneration={isAllBatchesHaveSingleGeneration}
          />
        ) : (
          <FilesViewOverlay
            showExpandToGalleryButton={true}
            showDownloadAllButton={!isTextAsset(selectedFile)}
            filesNavigationComponent={
              batchInfo ? (
                <BatchNavigation
                  batchNumber={batchInfo.batchNumber}
                  totalBatches={batchInfo.totalBatches}
                  onPrevious={handlePreviousBatch}
                  onNext={handleNextBatch}
                />
              ) : (
                <Box sx={{ pl: 1 }}>
                  {generations.length} {t(getAssetsTypeTranslationKey(generations))}
                </Box>
              )
            }
            isAllBatchesHaveSingleGeneration={isAllBatchesHaveSingleGeneration}
            onDownloadAll={handleDownload}
            onOpenGallery={handleOpenGallery}
            viewMode={viewMode}
            setViewMode={handleSetViewMode}
            selectedViewMode={selectedViewMode}
            isModelNode={true}
          />
        )}
      </GalleryGradientOverlay>
    </FlexCol>
  );
};
